import { Session } from 'data/session';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { disconnect, refresh } from 'services/auth_service';

export type Fn = () => void;

export interface AuthenticationCtx {
    session: Session | undefined;
    refresh: Fn;
    disconnect: Fn;
}

export const AuthenticationContext = createContext<AuthenticationCtx>({
    session: undefined,
    refresh: () => { },
    disconnect: () => { },
});

const AuthContextProvider: React.FC = (props) => {
    const [auth, setAuth] = useState<{
        session: Session,
    } | undefined>(undefined);

    const [refreshQueued, queueRefresh] = useState(false);
    const [refreshTimeout, setRefreshTimeout] = useState<NodeJS.Timeout | undefined>();

    const refreshToken = useCallback(() => {
        if (refreshTimeout) {
            clearTimeout(refreshTimeout);
        }
        setRefreshTimeout(undefined);
        refresh().then(session => {
            setTimeout(() => {
                queueRefresh(false);
            }, 5000);

            if (!session) {
                setAuth(undefined);
                return
            }

            setAuth({
                session: session,
            });

            setRefreshTimeout(
                setTimeout(() => {
                    queueRefresh(true);
                }, session.expires_in * 1000 - 5000)
            );
        });
    }, [refreshTimeout, setRefreshTimeout]);

    useEffect(() => {
        if (!refreshQueued) {
            return;
        }

        refreshToken();
    }, [refreshQueued]);

    const ctx = useMemo<AuthenticationCtx>(() => {
        return {
            session: auth?.session,
            refresh: () => {
                queueRefresh(true);
            },
            disconnect: () => {
                disconnect().then();
            },
        }
    }, [auth]);

    useEffect(() => {
        if (!ctx.session) {
            ctx.refresh();
        }
    }, [ctx]);

    return (
        <AuthenticationContext.Provider value={ctx}>
            {props.children}
        </AuthenticationContext.Provider>
    )
}

export default AuthContextProvider

